import { useCanAccess, useOidc, useScopeIds } from '@nohara-dxdev/react-oidc';
import { permission } from '@nohara-dxdev/oidc-core';
import { resolveOrgRoles } from '@common';

import type { PermissionOrGroup } from '@nohara-dxdev/oidc-core';

/**
 * Determines whether the current user is a 社外 (external) user for the org
 * currently being acted in.
 *
 * 社内/社外 is derived from the whoami permissions (from the OIDC context): a
 * user is internal when a grant scoped to the active org names a non-`external.*`
 * role in `conditions.name`. Everyone else — including a user who is internal in
 * some *other* org, or who holds both internal and external roles here — is
 * external. Fails closed: no org / no matching role reads as external.
 *
 * @returns `isExternal` and `isLoading` (true while the OIDC context loads).
 */
export function useIsExternalUser(): { isExternal: boolean; isLoading: boolean } {
  const { permissions, isLoading } = useOidc();
  // No-arg form returns undefined when no org is active (user hasn't selected
  // one); the `useScopeIds('org')` form throws in that case.
  const { orgId } = useScopeIds();
  if (!orgId) return { isExternal: true, isLoading };
  const resolved = resolveOrgRoles(
    permissions ?? [],
    orgId,
    (import.meta.env.VITE_APP_ID as string) ?? ''
  );
  if (resolved.isMixed) {
    console.warn(
      `useIsExternalUser: caller holds both internal and external roles for org ${orgId}; treating as external.`
    );
  }
  return { isExternal: !resolved.isInternal, isLoading };
}

/**
 * Returns true when the current user has *all* of the supplied permissions.
 *
 * Delegates to `useCanAccess`, which is the current react-oidc entry point
 * for permission checks. The previous implementation destructured `can`
 * off `useOidc()`, but that no longer exists on `OidcContextType` — the
 * context now exposes `ability` (Casl) and `useCanAccess` handles the
 * scope/subject plumbing.
 */
export function useHasPermission(requiredPermissions: readonly PermissionOrGroup[]): boolean {
  const { canAccess } = useCanAccess({ requireAll: [...requiredPermissions] });
  return canAccess;
}

/**
 * Permission constants for sekosuryo features
 * Provides type-safe permission references throughout the application
 */
export const sekosuroPermissions = {
  admin: {
    manageOrg: [permission['sekosuryo.organisation'].manage],
    manageUsers: [permission['sekosuryo.user'].manage],
  },
  calculation: {
    create: [permission['sekosuryo.calculation'].create],
    read: [permission['sekosuryo.calculation'].read],
    update: [permission['sekosuryo.calculation'].update],
    delete: [permission['sekosuryo.calculation'].delete],
  },
  masterData: {
    create: [permission['sekosuryo.master-data'].create],
    read: [permission['sekosuryo.master-data'].read],
    update: [permission['sekosuryo.master-data'].update],
    delete: [permission['sekosuryo.master-data'].delete],
  },
} as const;

/** Roles allowed to register a new project (物件登録). */
export const PROJECT_CREATE_ROLES = ['internal.admin', 'internal.operator3'] as const;

/**
 * Whether the current user holds one of `roleNames` for the org being acted in.
 *
 * Role-based, not permission-based: the OIDC context carries whatever HOME
 * granted, so a permission check can pass for a role sekosuryo does not intend
 * to allow. Gating on the resolved role name keeps the frontend aligned with
 * the backend, which maps role name → `sekosuryo.*` permissions in code.
 * Fails closed: no active org, or a mixed internal/external grant, reads as no.
 *
 * @returns `hasRole` and `isLoading` (true while the OIDC context loads).
 *   `hasRole` is false while loading — callers that render a screen (rather
 *   than a button) should show a loading state instead of the denied state, or
 *   an allowed user sees the denied UI until the context resolves.
 */
export function useHasOrgRole(roleNames: readonly string[]): {
  hasRole: boolean;
  isLoading: boolean;
} {
  const { permissions, isLoading } = useOidc();
  const { orgId } = useScopeIds();
  if (!orgId) return { hasRole: false, isLoading };
  const resolved = resolveOrgRoles(
    permissions ?? [],
    orgId,
    (import.meta.env.VITE_APP_ID as string) ?? ''
  );
  if (resolved.isMixed) {
    console.warn(
      `useHasOrgRole: caller holds both internal and external roles for org ${orgId}; treating as external, so internal roles are not matched.`
    );
  }
  return { hasRole: resolved.roles.some((name) => roleNames.includes(name)), isLoading };
}
